import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

import {
  fetchCompanySelectedStandards as fetchCompanySelectedStandardsService,
  fetchMitigationAssessmentReportsquery as fetchMitigationDetailsService
} from 'services/modules/cap/standardservice';

import {
  Box,
  Tabs,
  tabsClasses,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Paper,
  Tab
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import LoadingOverlay from 'components/LoadingOverlay.jsx';

import '../../styles/cap.css';
import AuditTrialPage from '../AuditTrialPage.jsx';
import { useNotification } from 'config/Notification';

const MitigationPlanPage = () => {
  // React router
  const navigate = useNavigate();

  // nOtification
  const { openNotification } = useNotification();

  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  
  // State variables
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [assessmentsList, setAssessmentsList] = useState([]);
  const [auditTrailPopup, setAuditTrailPopup] = useState(false);
  const [assessmentId, setAssessmentId] = useState('');
  const [value, setValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const status = ["Mitigation Response Submitted", "Review Mitigation Response", "Mitigation Initiated", "Mitigation Response In Progress", "Reject Mitigation Response"];
  
  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handles change in no of rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  
  // Handles change in selected standards
  const handleChange = (event, newValue) => {
    setValue((value) => value = newValue);
    if (newValue === 0) {
      getPendingAssessments(null);
    } else {
      getPendingAssessments(selectedStandards[newValue - 1].id);
    }
  };

  useEffect(() => {
    if (selectedCompanyContainer?.companyid !== null) {
      getCompanySelectedStandards();
      setValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);
  
  // Fetches the standards selected by company based on company id
  const getCompanySelectedStandards = async () => {
    try {
      setSelectedStandards([]);
      let params = { companyid: selectedCompanyContainer.companyid };
      const response = await fetchCompanySelectedStandardsService({ params });
      if (response !== '' && response.data !== undefined) {
        setSelectedStandards(response.data);
        getPendingAssessments(null);
      }
    } catch (error) {
      openNotification('Error while fetching standards', 'error')
    }
  };
  
  // Fetches all the assessments in mitigation plan
  const getPendingAssessments = async (standard) => {
    setLoading(true);
    setAssessmentsList([]);
    let assessmentsresponse = null;
    let statusArray = [...status, "Approved", "Mitigation In Progress"];
    let params = { company: selectedCompanyContainer.companyid, status: statusArray.join(',') };
    if (standard !== null)
      params.standard = standard;
    assessmentsresponse = await fetchMitigationDetailsService({ params });
    if (assessmentsresponse !== null) {
      setAssessmentsList(assessmentsresponse);
    }
    setLoading(false);
  };
  
  // Handles navigation to either assign mitigation or review mitigation based on assessment status
  const goToMitigation = (item) => {
    if (item.status === 'Approved' || item.status === 'Mitigation In Progress') {
      navigate('/cap/assignmitigation', { state: { assessmentid: item.assessment_id, standard: item.standardname, status: item.status } });

    } else if (status.includes(item.status)) {
      navigate('/cap/mitigationreview', { state: { assessmentid: item.assessment_id, standard: item.standardname, status: item.status } });
    }
  };

  return (
    <div>
      <div className="row py-1">
        <p>
          <img className="px-1 icons-style" src="../assets/icons/003-clipboard.svg" alt="View" />
          <b className="px-1">Assessments</b>
          <img className="px-1 icons-style" src="../assets/icons/right-layer.svg" alt="View" />
          Mitigations
        </p>
      </div>
      {loading && <LoadingOverlay />}
      <hr />
      <div className="full-width-field">
        {selectedStandards && selectedStandards.length > 0 && (
          <Box className="cap-tabs-section"
            sx={{
              flexGrow: 1,
              overflowX: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            <Tabs className="cap-tabs"
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  '&.Mui-disabled': { opacity: 0.3 },
                },
              }}
            >
              <Tab key={0} label="All" />
              {selectedStandards.map((tab, index) => (
                <Tab key={index + 1} label={tab.name} />
              ))}
            </Tabs>
          </Box>
        )}
        {assessmentsList && assessmentsList.length > 0 && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow >
                    <TableCell >Assessment id</TableCell>
                    <TableCell >User</TableCell>
                    <TableCell>Standard</TableCell>
                    <TableCell>Assigned date</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessmentsList.map((item, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell className="text-blue text-bold"
                          onClick={() => goToMitigation(item)}
                        >
                          {item.assessment_id}
                        </TableCell>
                        <TableCell>{item.username}</TableCell>
                        <TableCell>{item.standardname}</TableCell>
                        <TableCell>{new Date(item.created_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(item.due_date).toLocaleDateString()}</TableCell>
                        <TableCell>{item.status}
                          <Tooltip title="Go To Mitigation" placement="top">
                            <img src="../assets/icons/right-layer.svg" alt="arrow" className="px-2" onClick={() => goToMitigation(item)} />
                          </Tooltip>
                          <Tooltip title="View Audit Trail" placement="top">
                            <InfoIcon color="primary" fontSize="medium" onClick={() => {
                              setAssessmentId(item.assessment_id);
                              setAuditTrailPopup(!auditTrailPopup)
                            }} />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10,25,100]}
              component="div"
              count={assessmentsList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
        {assessmentsList && assessmentsList.length === 0 && (
          <Table>
            <TableRow>
              <TableCell colSpan={6}>
                <WarningIcon color="black" fontSize="small" />
                No data available
              </TableCell>
            </TableRow>
          </Table>
        )}
      </div>
      <div>
        {auditTrailPopup && (
          <AuditTrialPage
            toggleAuditTrial={() => setAuditTrailPopup(!auditTrailPopup)}
            assessmentId={assessmentId}
          />
        )}
      </div>
    </div>
  );
};

export default MitigationPlanPage;